import { SAVED_SEARCHES } from '../constants/fetchFailureTypes'
import _ from 'lodash'
import dataTableUtils from '../components/dataTable/utils'
import { SAVED_SEARCH_TYPES } from '../constants/savedSearches'
const { getFilterClausesFromSavedSearch } = dataTableUtils

/**
 * Used by dataTable actions to handle saved search actions (part of closure)
 */
export const initSavedSearchActions = (
  tableType,
  savedSearchProviders,
  tableActions
) => {
  /**
   * isDirty, when true, implies either (a) the user modified a search (cols/filters) or (b) they have
   * no search selected (which we leave dirty on so they can save anytime in that case.)  User's will not
   * be allowed to save when isDirty is false, as there's no need (it's the currently selected saved search)
   * @param {boolean} isDirty
   */
  const setSavedSearchDirty = isDirty => ({
    type: `STORE_SAVED_SEARCH_DIRTY_${tableType}`,
    isDirty,
  })

  /**
   * DirtyColumns holds the state of the columns (full definitions) temporarily while someone dirties a
   * saved search - so that (a) it keeps state on refresh and other changes and (b) we can revert back
   * to the original saved search easily or back to the non-saved search mode (from cookie) easily as well.
   * Also note - only updates when changed to avoid unnecessary updates
   * @param {array} dirtyColumns
   */
  const setSavedSearchDirtyColumns = dirtyColumns => {
    return (dispatch, getState) => {
      if (
        !_.isEqual(
          dirtyColumns,
          _.get(getState(), `${tableType}.savedSearches.dirtyColumns`)
        )
      ) {
        dispatch({
          type: `STORE_SAVED_SEARCH_DIRTY_COLUMNS_${tableType}`,
          dirtyColumns,
        })
      }
    }
  }

  /**
   * This initializes the savedSearch.searches with the content (array of saved searches) that we get from fetch.
   * @param {object} savedSearchData
   */
  const initSavedSearches = savedSearches => ({
    type: `STORE_SAVED_SEARCHES_${tableType}`,
    savedSearches,
  })

  const updateSavedSearch = (savedSearches, searchToUpdate) => ({
    type: `STORE_ONE_SAVED_SEARCH_${tableType}`,
    savedSearches,
    searchToUpdate,
  })

  const setSelectedSavedSearch = selectedSearchId => {
    return (dispatch, getState) => {
      let savedSearches = _.get(getState(), `${tableType}.savedSearches`)
      // if user picked same one and it's not dirty, nothing to update
      if (
        _.get(savedSearches, 'selectedSearchId') === selectedSearchId &&
        _.get(savedSearches, 'isDirty') === false
      )
        return

      // set id (value or null)
      dispatch(tableActions.setLoading(tableType, true))
      dispatch({
        type: `STORE_SELECTED_SEARCH_ID_${tableType}`,
        selectedSearchId,
      })

      if (selectedSearchId) {
        // get latest, now that we updated it above
        savedSearches = _.get(getState(), `${tableType}.savedSearches`)

        // store filters from saved search
        const filterClauses = getFilterClausesFromSavedSearch(savedSearches)
        if (filterClauses)
          dispatch(tableActions.replaceFilterClauses(filterClauses, tableType))

        dispatch(setSavedSearchDirty(false))
      } else {
        dispatch(tableActions.clearFilterClauses(tableType))
        dispatch(setSavedSearchDirty(true))
      }

      // NOTE - this relies on caller refreshing sort/data afterwards as necessary
    }
  }

  const fetchSavedSearches = () => {
    return async dispatch => {
      try {
        const savedSearches = await savedSearchProviders.getSavedSearches(
          null,
          tableType
        )
        const parsed = dataTableUtils.parseSavedSearchData(savedSearches)
        dispatch(initSavedSearches(parsed, tableType))
      } catch (err) {
        dispatch(tableActions.fetchFailure(err, tableType, SAVED_SEARCHES))
      }
    }
  }

  const persistSavedSearch = (dispatch, tableData, savedSearch) => {
    dispatch(setSavedSearchDirty(false))
    dispatch(setSavedSearchDirtyColumns(null))
  }

  const putSavedSearch = () => {
    return async (dispatch, getState) => {
      try {
        const tableData = getState()[tableType]
        dispatch(tableActions.setLoading(tableType, true))
        _.defer(async () => {
          try {
            // deferring so loading icon will render before we continue
            const payload = dataTableUtils.prepSavedSearchDataForPut(
              tableData.tableInfo,
              tableData.savedSearches
            )

            // Defensive: abort if columns would be saved as empty
            const parsedColumns = JSON.parse(payload.parameters.columns)
            if (parsedColumns.length === 0) {
              console.error(
                '[DataTable] putSavedSearch aborted: payload contains zero columns.',
                'This would corrupt the saved search. dirtyColumns:',
                tableData.savedSearches.dirtyColumns,
                'selectedSearchId:',
                tableData.savedSearches.selectedSearchId
              )
              dispatch(tableActions.setLoading(tableType, false))
              dispatch(
                tableActions.fetchFailure(
                  {
                    statusText:
                      'Save failed: column configuration was empty. Your saved search was not modified. Please re-size a column and try again.',
                  },
                  tableType,
                  SAVED_SEARCHES
                )
              )
              return
            }
            const savedSearch = await savedSearchProviders.putSavedSearch(
              null,
              payload
            )
            dispatch(tableActions.setLoading(tableType, false))
            persistSavedSearch(dispatch, tableData, savedSearch)

            // This updates the saved search list in redux, using the returned value from the put.
            dispatch(
              updateSavedSearch(
                _.get(tableData, 'savedSearches.searches', []),
                dataTableUtils.parseOneSavedSearch(savedSearch)
              )
            )
            dispatch(tableActions.setLoading(tableType, false))
          } catch (err) {
            dispatch(tableActions.fetchFailure(err, tableType, SAVED_SEARCHES))
          }
        })
      } catch (err) {
        dispatch(tableActions.fetchFailure(err, tableType, SAVED_SEARCHES))
      }
    }
  }

  const postSavedSearch = (
    searchName,
    savedSearchType = SAVED_SEARCH_TYPES.PERSONAL
  ) => {
    return async (dispatch, getState) => {
      try {
        const tableData = getState()[tableType]
        dispatch(tableActions.setLoading(tableType, true))
        _.defer(async () => {
          try {
            // deferring so loading icon will render before we continue
            const payload = dataTableUtils.prepSavedSearchDataForPost(
              tableData.tableInfo,
              tableData.savedSearches,
              searchName,
              tableType,
              savedSearchType
            )

            // Defensive: abort if columns would be saved as empty
            const parsedColumns = JSON.parse(payload.parameters.columns)
            if (parsedColumns.length === 0) {
              console.error(
                '[DataTable] postSavedSearch aborted: payload contains zero columns.',
                'This would create a corrupt saved search. dirtyColumns:',
                tableData.savedSearches.dirtyColumns
              )
              dispatch(tableActions.setLoading(tableType, false))
              dispatch(
                tableActions.fetchFailure(
                  {
                    statusText:
                      'Save failed: column configuration was empty. Please select at least one column and try again.',
                  },
                  tableType,
                  SAVED_SEARCHES
                )
              )
              return
            }
            const savedSearch = await savedSearchProviders.postSavedSearch(
              null,
              payload
            )

            persistSavedSearch(dispatch, tableData, savedSearch)

            // This updates the saved search list in redux, using the returned value from the post.
            dispatch(
              initSavedSearches([
                dataTableUtils.parseOneSavedSearch(savedSearch),
                ..._.get(tableData, 'savedSearches.searches', []),
              ])
            )
            dispatch(setSelectedSavedSearch(savedSearch.saved_search_id))
            dispatch(tableActions.setLoading(tableType, false))
          } catch (err) {
            dispatch(tableActions.fetchFailure(err, tableType, SAVED_SEARCHES))
          }
        })
      } catch (err) {
        dispatch(tableActions.fetchFailure(err, tableType, SAVED_SEARCHES))
      }
    }
  }

  const deleteSavedSearch = id => {
    return async (dispatch, getState) => {
      try {
        const tableData = getState()[tableType]
        dispatch(tableActions.setLoading(tableType, true))

        // deferring so loading icon will render before we continue
        _.defer(async () => {
          try {
            // This updates the saved search list in redux, omitting the deleted one,
            // calling this before actual delete as delete can be slow
            dispatch(
              initSavedSearches(
                _.reject(_.get(tableData, 'savedSearches.searches', []), {
                  saved_search_id: id,
                })
              )
            )
            await savedSearchProviders.deleteSavedSearch(null, id)
            dispatch(tableActions.setLoading(tableType, false))
          } catch (err) {
            dispatch(tableActions.fetchFailure(err, tableType, SAVED_SEARCHES))
          }
        })
      } catch (err) {
        dispatch(tableActions.fetchFailure(err, tableType, SAVED_SEARCHES))
      }
    }
  }

  return {
    fetchSavedSearches,
    putSavedSearch,
    postSavedSearch,
    deleteSavedSearch,
    setSavedSearchDirty,
    setSavedSearchDirtyColumns,
    setSelectedSavedSearch,
  }
}
